import { PortableTextBody } from "../components/PortableText";
import { PortableTextBlock } from "../components/PortableText/PortableText";

/**
 * Dictionary of placeholder names and the corresponding
 * text to replace it with, e.g.
 * {
 *  city: "Stockholm"
 * }
 */
export type PlaceholderTextMap = {
  [placeholder: string]: string;
};

export const GeographicalNamePlaceholder = "geo_name";

/**
 * Matches [placeholder] and replaces it with the text value from the placeholers map object
 * if it exists.
 * @param text string that may contain a placeholder surrounded by []
 * @param placeholderTextMap map with values to replace each placeholder with
 * @returns string with placeholders replaced by the mapped string
 */
export const replacePlaceholders = (
  text: string,
  placeholderTextMap: PlaceholderTextMap
): string =>
  text && typeof text === "string"
    ? text.replace(/\[(\w+)\]/gi, (match, group) => placeholderTextMap[group] ?? match)
    : text;

/**
 * Replaces placesholders in spans in portable text
 * @param body portable text body
 * @param placeholderTextMap map with placeholder names and replacement strings
 * @returns copy of body with placeholders replaced by their text value
 */
export const replacePortableTextPlaceholders = (
  body: PortableTextBody,
  placeholderTextMap: PlaceholderTextMap
): PortableTextBody =>
  Array.isArray(body)
    ? body.map(block => replacePortableTextBlockPlaceholders(block, placeholderTextMap))
    : body;

export const replacePortableTextBlockPlaceholders = (
  block: PortableTextBlock,
  placeholderTextMap: PlaceholderTextMap
): PortableTextBlock =>
  block
    ? {
        ...block,
        children: block.children?.map(child =>
          child
            ? {
                ...child,
                ...(child._type === "span" &&
                  child.text && {
                    text: replacePlaceholders(child.text, placeholderTextMap)
                  })
              }
            : child
        )
      }
    : block;

export interface PlaceholderBlock extends PortableTextBlock {
  answer?: PortableTextBody;
  portableTextContent?: PortableTextBody;
  question?: string;
  subtitle?: string;
  title?: string;
}

export const replaceCommonBlockPlaceholders = (
  block: PlaceholderBlock,
  placeholderTextMap: PlaceholderTextMap
): PlaceholderBlock =>
  block // block can be null if resolveReferences can't resolve the type (e.g. newly added type)
    ? {
        ...block,
        ...(block.answer && {
          answer: replacePortableTextPlaceholders(block.answer, placeholderTextMap)
        }),
        ...(block.portableTextContent && {
          portableTextContent: replacePortableTextPlaceholders(
            block.portableTextContent,
            placeholderTextMap
          )
        }),
        ...(block.question && {
          question: replacePlaceholders(block.question, placeholderTextMap)
        }),
        ...(block.subtitle && {
          subtitle: replacePlaceholders(block.subtitle, placeholderTextMap)
        }),
        ...(block.title && {
          title: replacePlaceholders(block.title, placeholderTextMap)
        })
      }
    : block;
